import React, { useState } from "react";

import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { Alert, Box, Divider, Typography, useMediaQuery, useTheme, Stack, IconButton } from "@mui/material";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";

import ChartModal from "./ChartModal";

interface IProps {
  title: string;
  children: React.ReactNode;
  alertMessage?: string;
  FallbackComponent: React.ComponentType<FallbackProps>;
}

const ChartWrapper = ({ title, children, alertMessage, FallbackComponent }: IProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [fullScreen, setFullScreen] = useState(false);

  if (!children && !alertMessage) {
    return null;
  }

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6" component="h1" fontWeight={600} sx={{ color: "tertiary.main", py: 3 }}>
          {title}
        </Typography>
        {!isMobile && children && (
          <IconButton size="small" onClick={() => setFullScreen(true)}>
            <FullscreenIcon />
          </IconButton>
        )}
      </Stack>
      <Divider sx={{ mb: 1 }} />
      {children ? (
        <Box alignItems="center">
          <ErrorBoundary FallbackComponent={FallbackComponent}>{children}</ErrorBoundary>
        </Box>
      ) : (
        alertMessage && (
          <Alert severity="info" sx={{ mt: 3 }}>
            {alertMessage}
          </Alert>
        )
      )}
      {fullScreen && (
        <ChartModal title={title} handleClose={() => setFullScreen(false)}>
          <ErrorBoundary FallbackComponent={FallbackComponent}>
            {React.isValidElement(children) ? React.cloneElement(children, { isFullScreen: true } as any) : children}
          </ErrorBoundary>
        </ChartModal>
      )}
    </>
  );
};

export default ChartWrapper;
